import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * Restores the markdown-paste behavior that v2's `tiptap-markdown`
 * (`transformPastedText: true`) provided and that @tiptap/markdown v3 dropped:
 * pasting PLAIN-TEXT markdown (`# heading`, `- bullet`, `> quote`, etc.) renders
 * as rich nodes instead of literal text. The conversion goes through the editor's
 * own markdown engine (`insertContent(..., { contentType: 'markdown' })`), so
 * block AND inline markdown convert consistently with how notes load from storage.
 *
 * Scope (ENG-788 fast-follow): ALL plain-text paste runs through the markdown
 * engine (v2 parity). Because `marked` (gfm) passes raw HTML through and
 * @tiptap/markdown re-parses it against the editor schema, pasting raw HTML source
 * also renders — mapped to known nodes/marks, unknown tags dropped (so it's
 * schema-sanitized, not arbitrary HTML). Rich-text paste (a `text/html` clipboard,
 * e.g. from a web page) is left to ProseMirror's native HTML parsing, and paste
 * inside a code context stays literal.
 */
export const MarkdownPaste = Extension.create({
  name: 'markdownPaste',

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin({
        key: new PluginKey('markdownPaste'),
        props: {
          handlePaste: (_view, event) => {
            const text = event.clipboardData?.getData('text/plain') ?? '';
            const html = event.clipboardData?.getData('text/html') ?? '';

            // Rich-text paste (carries text/html) → native HTML parsing.
            // Empty plain text → nothing to convert.
            if (html || !text.trim()) {
              return false;
            }

            // Inside a code block / inline code, paste must stay literal.
            if (editor.isActive('codeBlock') || editor.isActive('code')) {
              return false;
            }

            try {
              // @tiptap/markdown augments insertContent to accept
              // `contentType: 'markdown'` — it parses via the editor's markdown
              // engine, then inserts the nodes over the current selection.
              return editor.commands.insertContent(text, {
                contentType: 'markdown',
              });
            } catch {
              // Any parse failure → fall back to ProseMirror's default paste.
              return false;
            }
          },
        },
      }),
    ];
  },
});
